/**
 * usePresence - Hook to fetch real-time player presence for Travel UI
 * 
 * Provides player counts per scene without requiring WebSocket connection.
 * Polls at a reasonable interval to keep counts fresh.
 */

import { useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const POLL_INTERVAL_MS = 10000; // Poll every 10 seconds

/**
 * Format presence text for display
 * @param {number} count - Number of players
 * @returns {string} Human-readable presence text
 */
export const formatPresenceText = (count) => {
  if (count === 0) return 'No travelers currently here';
  if (count === 1) return '1 traveler currently here';
  return `${count} travelers currently here`;
};

/**
 * Hook to get presence data for all scenes
 * @param {boolean} enabled - Whether to enable polling (default true)
 * @returns {{ presence: object, loading: boolean, error: string|null, refetch: function }}
 */
export function usePresence(enabled = true) {
  const [presence, setPresence] = useState({
    total_online: 0,
    scenes: {},
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPresence = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/multiplayer/presence`);
      if (!response.ok) {
        throw new Error('Failed to fetch presence');
      }
      const data = await response.json();
      setPresence(data);
      setError(null);
    } catch (err) {
      console.warn('usePresence: Failed to fetch', err);
      setError('Unable to load presence');
      // Don't clear existing presence data on error
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;

    // Initial fetch
    fetchPresence();

    // Set up polling
    const interval = setInterval(fetchPresence, POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [enabled, fetchPresence]);

  return { presence, loading, error, refetch: fetchPresence };
}

/**
 * Get presence count for a specific scene
 * @param {object} presence - Presence data from usePresence
 * @param {string} sceneId - Scene ID to look up
 * @returns {number} Player count (0 if not found)
 */
export const getSceneCount = (presence, sceneId) => {
  return presence?.scenes?.[sceneId]?.count || 0;
};

/**
 * Map settlement IDs to their canonical scene IDs
 * Used to connect Travel UI locations to multiplayer scene presence
 */
export const SETTLEMENT_TO_SCENE_MAP = {
  // Thornhaven settlement maps to thornhaven-exterior scene
  'thornhaven': 'thornhaven-exterior',
  // Locations within Thornhaven
  'thornhaven-tavern': 'golden-mead-hall',
  'golden-mead-hall': 'golden-mead-hall',
};

/**
 * Get presence count for a settlement (mapping to scene ID)
 * @param {object} presence - Presence data from usePresence
 * @param {string} settlementId - Settlement ID from Travel UI
 * @returns {number} Player count (0 if not found)
 */
export const getSettlementPresence = (presence, settlementId) => {
  const sceneId = SETTLEMENT_TO_SCENE_MAP[settlementId] || settlementId;
  return getSceneCount(presence, sceneId);
};

export default usePresence;
